import { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '../App';

const DEFAULT_PERMISSIONS = {
  activities: true,
  nutrition: true,
  daily_logs: true,
  development: true,
  nutritional_status: true,
  children: true,
  attendance: true,
  reports: true,
  invoices: true,
  takwim: true
};

// Cache permissions to avoid repeated API calls
const permissionsCache = new Map();

export const useFeaturePermissions = (userRole) => {
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches
    if (fetchedRef.current) return;
    
    const fetchPermissions = async () => {
      // Admin and Superadmin have all permissions
      if (userRole === 'admin' || userRole === 'superadmin') {
        setPermissions(DEFAULT_PERMISSIONS);
        setLoading(false);
        fetchedRef.current = true;
        return;
      }

      // Check cache first
      if (permissionsCache.has(userRole)) {
        setPermissions(permissionsCache.get(userRole));
        setLoading(false);
        fetchedRef.current = true;
        return;
      }

      try {
        const res = await axiosInstance.get('/user/permissions');
        const userPermissions = res.data.permissions || DEFAULT_PERMISSIONS;
        permissionsCache.set(userRole, userPermissions);
        setPermissions(userPermissions);
      } catch (error) {
        console.log('Error fetching permissions, using defaults');
        setPermissions(DEFAULT_PERMISSIONS);
      } finally {
        setLoading(false);
        fetchedRef.current = true;
      }
    };

    fetchPermissions();
  }, [userRole]);

  const hasAccess = useCallback((featureId) => {
    return permissions[featureId] !== false;
  }, [permissions]);

  const refetch = useCallback(async () => {
    fetchedRef.current = false;
    permissionsCache.delete(userRole);
    setLoading(true);
  }, [userRole]);

  return { permissions, loading, hasAccess, refetch };
};

export default useFeaturePermissions;
